/**
 * Innovation Domain
 * Types, store, handlers and utilities for the tech tree / innovation system
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { handleTeamUpdate } from './teams';
import type { Team } from './teams';
import { handleKnowledgePointsUpdate, teamPlayersOnline, type OnlinePlayerData } from './players';
import { getCurrentCohortId, getViewerUserId } from './current-team';
import { sendGatedCommand } from './command-gate';
import { handleAvailablePlanesUpdate, handleFleetUpdate, type Fleet, type AvailablePlanes } from './fleets';
import { handleSegmentUpdate, type Segment } from './segments';
import { OpCode, MessageType } from '../constants/op-codes';
import { showErrorToast } from './toasts';
import { setAirplaneModels, type AirplaneModel } from '../data/airplanes';
import { prerequisiteOf } from '../data/tech-tree-data';
import i18n from '../i18n/config';
import { formatDecimal } from '../utils/format';
import { refusalKey, refusalText, type OrderBlock, type OrderRefusal } from './order-block';

// Define the message type locally to avoid circular import issues
interface UnlockInnovationSuccessMessage {
  type: string;
  /** Who asked for the unlock. Every player hears of it; only this one was waiting. */
  sender_user_id?: string;
  innovation_id: string;
  team: Team;
  cohort_id: string;
  fleet?: Fleet;
  segments?: Segment[];
  available_planes?: AvailablePlanes;
  airplane_models?: AirplaneModel[];
  copay?: { user_id: string; amount_paid: number; new_knowledge_points: number }[];
}

// ==================== STORE ====================

interface InnovationState {
  /** The innovation a research request of yours is on its way for, until the server answers. */
  pendingId: string | null;

  // Actions
  setPendingId: (innovationId: string | null) => void;
  reset: () => void;
}

const initialState: Pick<InnovationState, 'pendingId'> = {
  pendingId: null,
};

export const useInnovationStore = create<InnovationState>()(
  devtools(
    (set) => ({
      ...initialState,

      setPendingId: (innovationId) => {
        set({ pendingId: innovationId }, false, 'setPendingId');
      },

      reset: () => {
        set(initialState, false, 'reset');
      },
    }),
    {
      name: 'innovation-store',
    }
  )
);

// ==================== SELECTORS ====================

/** One player's share of a research's cost, in knowledge points. */
interface KnowledgeShare {
  userId: string;
  amount: number;
}

/** A player in a research's pool, and the knowledge they hold. */
interface KnowledgePayer {
  userId: string;
  knowledge: number;
}

/**
 * How the server splits a research's cost between whoever is on its Research
 * (PlanKnowledgePointsCopay, innovation.go): the player who presses first,
 * then the teammates on it in order of user id. Equal shares, the first few a
 * point more where the cost does not divide; each share capped at what its
 * player holds, and what that leaves taken from whoever still has some, in
 * that order. `null` when they cannot cover it between them — the server
 * refuses it then. knowledge-split-cases.json holds this and the server to
 * the same answers.
 */
export function planKnowledgeSplit(buyer: KnowledgePayer, teammates: readonly KnowledgePayer[], cost: number): KnowledgeShare[] | null {
  if (cost <= 0) return [];
  const payers = [
    buyer,
    ...teammates.filter((teammate) => teammate.userId !== buyer.userId).sort((a, b) => (a.userId < b.userId ? -1 : a.userId > b.userId ? 1 : 0)),
  ];
  const share = Math.floor(cost / payers.length);
  const odd = cost - share * payers.length;
  const amounts = payers.map((payer, index) => Math.min(share + (index < odd ? 1 : 0), payer.knowledge));
  let missing = cost - amounts.reduce((sum, amount) => sum + amount, 0);
  for (let index = 0; index < payers.length && missing > 0; index++) {
    const take = Math.min(payers[index].knowledge - amounts[index], missing);
    if (take <= 0) continue;
    amounts[index] += take;
    missing -= take;
  }
  if (missing > 0) return null;
  return payers.map((payer, index) => ({ userId: payer.userId, amount: amounts[index] }));
}

/**
 * The knowledge the team's players online hold between them: the most one
 * Research could pool, with every one of them on it.
 */
export function teamKnowledge(
  players: Readonly<Record<string, Pick<OnlinePlayerData, 'user_id' | 'cohort_id' | 'knowledge_points'>>>,
  disconnected: ReadonlySet<string>,
  cohortId: string | null | undefined,
): number {
  return teamPlayersOnline(players, disconnected, cohortId).reduce((sum, player) => sum + (player.knowledge_points ?? 0), 0);
}

/**
 * Why a research was refused, by the code the server sends beside its English
 * (innovation.go): the moderator's switch, a teammate's research landing
 * first, a prerequisite or a connection the tree had not caught up with, or a
 * pool short of the cost.
 */
const INNOVATION_REFUSALS = {
  spend_locked: 'innovation.spendLocked',
  researched: 'innovation.refusal.researched',
  requires: 'innovation.requires',
  not_connected: 'innovation.connectFirst',
  knowledge: 'innovation.refusal.knowledge',
} as const;

/**
 * What a refused research tells the player, for the innovation it asked for:
 * the key that says its reason, with the prerequisite named or the knowledge
 * counted. Null when the server sent no reason the client knows — a failure
 * no player meets in play, or a code from a newer server — or a reason that
 * lost what it names, and the server's words stand.
 */
export function readInnovationRefusal({ reason, need, have }: OrderRefusal, innovationId: string | null): OrderBlock | null {
  const key = refusalKey(INNOVATION_REFUSALS, reason);
  if (key === undefined) return null;
  if (reason === 'requires') {
    const prerequisite = prerequisiteOf(innovationId);
    return prerequisite ? { key, values: { name: i18n.t(`innovation.nodes.${prerequisite}.name`) } } : null;
  }
  if (reason === 'knowledge') {
    if (typeof need !== 'number' || typeof have !== 'number') return null;
    return { key, values: { need: formatDecimal(need), have: formatDecimal(have) } };
  }
  return { key };
}

// ==================== HANDLERS ====================

/**
 * Handle successful innovation unlock
 */
export function handleUnlockInnovationSuccess(data: UnlockInnovationSuccessMessage) {
  console.log('[InnovationHandler] Innovation unlocked:', data.innovation_id, 'for team:', data.cohort_id);

  // Every player hears of every team's unlocks. Only your own request's reply
  // ends your wait; another player's success says nothing about yours.
  const store = useInnovationStore.getState();
  if (data.sender_user_id === undefined || data.sender_user_id === getViewerUserId()) {
    store.setPendingId(null);
  }
  
  // Update team data - team.unlocked_innovations is the canonical source of truth
  // for which innovations are unlocked.
  if (data.team) {
    handleTeamUpdate(data.cohort_id, data.team);
  }

  // Apply per-player KP deductions from the copay split (one entry per contributor).
  if (data.copay && data.copay.length > 0) {
    for (const entry of data.copay) handleKnowledgePointsUpdate(entry.user_id, entry.new_knowledge_points);
  }
  
  // Update fleet if included (plane innovations upgrade fleet slots)
  if (data.fleet) {
    console.log('[InnovationHandler] Updating fleet with upgraded plane data');
    handleFleetUpdate(data.cohort_id, data.fleet);
  }
  
  // Update available planes if included (deals like manufacturer_relations update plane costs)
  if (data.available_planes) {
    console.log('[InnovationHandler] Updating available planes with modified costs:', data.available_planes);
    handleAvailablePlanesUpdate(data.cohort_id, data.available_planes);
  }
  
  // Update segments if included (plane innovations update segment stats)
  if (data.segments && data.segments.length > 0) {
    console.log('[InnovationHandler] Updating', data.segments.length, 'segments with upgraded plane data');
    for (const segment of data.segments) handleSegmentUpdate(segment);
  }
  
  // Update airplane models if included (fleet innovations modify model specs).
  // They are the researching team's models, upgrades applied, and each client
  // holds its own team's: taken from another team, they would fly that team's
  // upgraded aircraft on this player's screens until they rejoined.
  if (data.cohort_id === getCurrentCohortId() && data.airplane_models && data.airplane_models.length > 0) {
    console.log('[InnovationHandler] Updating airplane models with fleet innovation modifiers');
    setAirplaneModels(data.airplane_models);
  }
}

/**
 * Handle failed innovation unlock: say why in a toast, where the game's
 * passing failures go — what could not be researched, and the reason in the
 * player's language when the server sent one the client knows.
 */
export function handleUnlockInnovationFailed(data: OrderRefusal) {
  console.error('[InnovationHandler] Innovation unlock failed:', data.reason ?? '', data.error);

  const store = useInnovationStore.getState();
  const asked = store.pendingId;
  store.setPendingId(null);
  const title = asked ? i18n.t('innovation.refusal.title', { name: i18n.t(`innovation.nodes.${asked}.name`) }) : i18n.t('innovation.refusal.titleAny');
  showErrorToast(title, refusalText(readInnovationRefusal(data, asked), data));
}

// ==================== SERVICE FUNCTIONS ====================

/**
 * Request to unlock an innovation.
 * Sends a message to the server which validates the request, splits the Knowledge
 * Points cost across the buyer plus any teammates hovering the innovation's
 * button (copay) and broadcasts the result. The server derives that button from
 * the innovation — `innovationButtonId` in constants/server-decisions.ts — so
 * the request names the innovation alone.
 *
 * @param innovationId - ID of the innovation to unlock
 */
export async function unlockInnovation(innovationId: string): Promise<void> {
  const store = useInnovationStore.getState();
  store.setPendingId(innovationId);

  const message = {
    type: MessageType.MessageTypeUnlockInnovation,
    innovation_id: innovationId,
  };

  console.log('[InnovationService] Requesting unlock:', innovationId);

  await sendGatedCommand(OpCode.OpCodeUnlockInnovation, JSON.stringify(message), {
    endWait: () => store.setPendingId(null),
    notSent: handleUnlockInnovationFailed,
  });
}
